<script setup lang="ts">
import { computed, ref } from 'vue';
import { clearNull, formatNameInitials } from '@/utils/main';
import store from "@/store";
import VitaDrop from '@/components/UI/VitaDrop.vue';
import VitaIcon from './UI/VitaIcon.vue';
import VitaPrintTable from '@/components/VitaPrintTable.vue';
import VitaInfoModal from '@/components/UI/VitaInfoModal.vue';
import VitaReportDrop from '@/components/VitaReportDrop.vue';
import AuthenticationRestoreDialog from '@/components/AuthenticationRestoreDialog.vue';
import router from '@/router';
import { isNavigationFailure, NavigationFailureType } from 'vue-router';
import { reachGoal } from '@/utils/metrika';

const userName = computed(() => {
  return clearNull(store.getters["user/userName"]);
})

const userPosition = computed(() => {
  return store.getters['user/userPosition']
})

const userPositionBid = computed(() => {
  return store.getters['user/userPositionBid']
})

const isGurvRole = computed<boolean>(() => {
  return store.getters['getGurvRole']
})

const isOffice = computed(() => {
  return store.getters['getOfficeState']
})

const isBeruAccessRole = computed<boolean>(() => {
  return store.getters['getBeruAccessRole']
})

const modalTitle = ref<string>('Вы действительно хотите выйти?')
const openLogoutModal = ref<boolean>(false)
const openPasswordModal = ref<boolean>(false)
const openSettingsDrop = ref<boolean>(false)
const openReportDrop = ref<boolean>(false)
const printModal = ref({ open: false })
const inProcess = ref(false);

const toggleSettingsDrop = () => {
  openSettingsDrop.value = !openSettingsDrop.value
}

const closeSettingsDrop = () => {
  openSettingsDrop.value = false
}

const toggleReportDrop = () => {
  openReportDrop.value = !openReportDrop.value
  reachGoal('sidenav-report-click')
}

const closeReportDrop = () => {
  openReportDrop.value = false
}

const closePrintModal = () => {
  printModal.value.open = false
}

const openPrintModal = () => {
  printModal.value.open = true
}

const logout = async () => {
  try {
    inProcess.value = true
    await store.dispatch('user/logout')
    store.commit('clearDates') // Сбрасываем даты в состоянии, чтобы предотвратить сохранение после выхода
    store.commit('clearShiftBuffer') // Сбрасываем скопированные смены в состоянии
    // Очистка поиска аптек
    store.commit('data/setPharmSkip', 0)
    store.commit('data/setPharmSearchQuery', '')

    // Очистка поиска внешних пользователей
    store.commit('data/setUserSearchSkip', 0)
    store.commit('data/setUserSearchList', null)
    store.commit('setUnsavedChanges', false)
    // Очистка открытых модалок создания смен
    store.commit('data/clearOpenedCreateShift')
    // Сброс меню
    store.commit('resetSidebarState')
    reachGoal('sidenav-logout-click')
  } catch (e) {
    console.error(e)
  } finally {
    inProcess.value = false
  }
}

const collapsedSidebar = computed(() => store.getters['getSidebarState'])
const collapseBtnTitle = computed(() => {
  return collapsedSidebar.value ? 'Раскрыть панель меню' : undefined
})

const toggleSidebar = (e: Event) => {
  e.preventDefault()
  store.commit('setSidebarState', !collapsedSidebar.value)
  reachGoal('sidenav-toggle-click')
}

const toggleSidebarOnBlankSpace = (e: MouseEvent) => {
  if (!(e.target instanceof Element)) return

  const interactiveElement = e.target.closest('button, a, input, textarea, select, [data-interactive]')
  if (interactiveElement) return

  store.commit('setSidebarState', !collapsedSidebar.value)
  reachGoal('sidenav-click')
}

const openBeruService = () => {
  router.push('/beru').then((failure) => {
    if (isNavigationFailure(failure, NavigationFailureType.duplicated)) return
    reachGoal('sidenav-beru-route')
  });
}

const openTimetable = () => {
  router.push('/').then((failure) => {
    if (isNavigationFailure(failure, NavigationFailureType.duplicated)) return
    reachGoal('sidenav-timetable-route')
  });
}

const onPassChangeClick = () => {
  openPasswordModal.value = true
  reachGoal('sidenav-change-password-click')
}
</script>

<template>
  <div class="side-nav" @click="toggleSidebarOnBlankSpace" data-qa="side-nav-blank-space-toggle">
    <div class="side-nav-logo">
      <img width="48" height="48" alt="Вита" src="@/static/img/logo-icon.svg" />
    </div>
    <div class="side-nav-body">
      <div>
        <ul class="side-nav-items">
          <li>
            <button class="side-nav-btn" type="button" title="График" @click="openTimetable" data-interactive
                    data-qa="side-nav-button-timetable">
              <vita-icon name="schedule"/>
              <span>График</span>
            </button>
          </li>
          <li v-if="$route.path !== '/beru'">
            <button class="side-nav-btn" type="button" @click="openPrintModal" title="Печать" data-interactive
                    data-qa="side-nav-button-print">
              <vita-icon name="print-lg"/>
              <span>Печать</span>
            </button>
            <vita-info-modal :open="printModal.open" @toggleInfoModal="closePrintModal" title="Печатный вид графика" :modal-icon="false" modal-size="medium">
              <vita-print-table></vita-print-table>
            </vita-info-modal>
          </li>
          <li v-if="isGurvRole">
            <button :class="{'active' : openReportDrop, 'side-nav-btn' : true}"
                    type="button"
                    @click="toggleReportDrop"
                    title="Отчет"
                    data-interactive
                    data-qa="side-nav-button-report-toggle"
            >
              <vita-icon name="calendar"/>
              <span>Отчет</span>
            </button>
            <Transition name="fade-ease">
              <VitaDrop :isOpen="openReportDrop" v-click-outside="closeReportDrop" class="report-drop">
                <VitaReportDrop :is-open="true"/>
              </VitaDrop>
            </Transition>
          </li>
          <li v-if="isBeruAccessRole">
            <button class="side-nav-btn" type="button" title="Сервис Беру!" @click="openBeruService" data-interactive
                    data-qa="side-nav-button-beru">
              <vita-icon name="service-beru"/>
              <span>Сервис <br> Беру!</span>
            </button>
          </li>
        </ul>
      </div>
      <div>
        <ul class="side-nav-items secondary">
          <li>
            <button :class="{'active' : openSettingsDrop, 'side-nav-btn' : true}"
                    type="button"
                    @click="toggleSettingsDrop"
                    title="Настройки"
                    data-interactive
                    data-qa="side-nav-button-settings-toggle"
            >
              <vita-icon name="settings"/>
              <span>Настройки</span>
            </button>
            <Transition name="fade-ease">
              <VitaDrop :isOpen="openSettingsDrop" v-click-outside="closeSettingsDrop" class="settings-drop">
                <vita-button @click="onPassChangeClick()" color="blue" v-if="!isOffice"
                             data-qa="side-nav-button-change-password">Сменить пароль</vita-button>
                <vita-button @click="openLogoutModal = true" color="black"
                             data-qa="side-nav-button-logout">
                  <span class="mr-10">Выйти</span>
                  <vita-icon name="enter-big"/>
                </vita-button>
              </VitaDrop>
            </Transition>
            <vita-modal :title="modalTitle" v-if="openLogoutModal">
              <vita-button color="red" width="inline" :disabled="inProcess" @click="logout"
                           data-qa="side-nav-button-logout-confirm">Да</vita-button>
              <vita-button color="blue" width="inline" :disabled="inProcess" @click="openLogoutModal = false"
                           data-qa="side-nav-button-logout-cancel">Нет</vita-button>
            </vita-modal>
            <vita-modal v-if="openPasswordModal">
              <div class="modal-inner">
                <vita-icon class="modal-close" name="cross-dark" @click="openPasswordModal = false"
                           data-qa="side-nav-icon-password-modal-close" />
                <AuthenticationRestoreDialog :soft-reset="true"/>
              </div>
            </vita-modal>
          </li>
          <li class="user-item">
            <div class="user-popup-wrapper" data-interactive>
              <div class="side-nav-user">
                {{ formatNameInitials(userName) }}
              </div>
              <div class="user-popup" v-if="userName || userPosition">
                <div class="user-popup-title">{{userName}}</div>
                <div class="user-popup-subtitle" v-if="userPosition">
                  <span>{{userPosition}}</span>
                  <span v-if="userPositionBid">({{userPositionBid}})</span>
                </div>
              </div>
            </div>
          </li>
        </ul>
        <button class="side-nav-control" type="button" @click="toggleSidebar($event)" :title="collapseBtnTitle" data-interactive
                data-qa="side-nav-button-collapse-toggle">
          <svg width="13" height="10" viewBox="0 0 13 10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.5 5L5.55735 4.12046e-08L6.5 0.931958L2.38528 5L6.5 9.06804L5.55735 10L0.5 5Z" fill="white"/>
            <path d="M6.5 5L11.5573 4.12046e-08L12.5 0.931958L8.38528 5L12.5 9.06804L11.5573 10L6.5 5Z" fill="white"/>
          </svg>
          <span>Скрыть</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.side-nav {
  --sidebar-width: 116px;
  width: var(--sidebar-width);
  background: rgba(48, 168, 255, 1);
  display: flex;
  flex-direction: column;
  font-size: 13px;
  font-weight: 500;
  line-height: 15.85px;
  text-align: center;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  box-shadow: -1px 0px 12px 0px rgba(0, 0, 0, 0.15);
  border-radius: 0 3px 3px 0;
  margin-bottom: -24px;
  height: 100vh;

  &:not(:hover) .user-popup {
    display: none;
  }

  &:hover {

    &::before, &::after {
      opacity: 0;
    }

    & > div {
      opacity: 1;
    }
  }
}

.side-nav-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 30px;
  flex: 1 0 auto;
  padding-top: 18px;
  padding-bottom: 8px;
}

.side-nav-items {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;

  &.secondary {
    gap: 18px;

    &:not(:last-child) {
      margin-bottom: 29px;
    }
  }

  > li {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-align: center;
    color: #ffffff;
    position: relative;

    &.user-item {
      cursor: default;
    }

    > button {
      -webkit-appearance: none;
      appearance: none;
      background-color: transparent;
      border: 1px solid transparent;
      transition: background-color .15s ease, border-color .15s ease;
      border-radius: 3px;
      color: #fff;
      font-family: inherit;
      cursor: pointer;
      user-select: none;
      min-width: 92px;
      min-height: 78px;
      padding: 10px 8px;

      &:hover {
        background-color: rgba(255, 255, 255, 0.15);
      }

      &:active {
        background-color: rgba(255, 255, 255, 0.15);
        border-color: rgba(255, 255, 255, 0.3);
      }

      &.active {
        background-color: rgba(255, 255, 255, 0.15);
      }

      span {
        display: block;
        margin-top: 8px;
        line-height: 16px;
        font-weight: 500;
        font-size: 13px;
      }
    }
  }
}

.side-nav-logo {
  border-bottom:  1px solid rgba(255, 255, 255, 0.3);
  padding: 20px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.side-nav-user {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  border-radius: 50%;
  width: 38px;
  height: 38px;
  background: rgba(255, 255, 255, 1);
  font-style: normal;
  font-weight: 600;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: 0;
  color: #333333;
}

.side-nav-control {
  background-color: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  padding: 0 16px;
  border-radius: 3px;
  gap: 8px;
  min-height: 20px;
  opacity: .85;
  color: #FFFFFF;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0;
  cursor: pointer;
  user-select: none;
  transition: background-color .3s, opacity .3s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
    opacity: 1;
  }

  &:active {
    background-color: rgba(255, 255, 255, 0.3);
    opacity: 1;
  }
}

.user-popup {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: calc(100% + 16px);
  z-index: 1001;
  min-width: max-content;
  min-height: 71px;
  background: #FFFFFF;
  color: #333333;
  border-radius: 3px;
  box-shadow: -1px 0px 12px 0px rgba(0, 0, 0, 0.15);
  padding: 16px 12px;
  transition: .3s ease;
  text-align: left;
  visibility: hidden;
  opacity: 0;

  & > div {
    max-width: 300px;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: -14px;
    width: 16px;
    height: 20px;
    background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTQiIGhlaWdodD0iMjEiIHZpZXdCb3g9IjAgMCAxNCAyMSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE0IDIwLjVMMTQgMC41TDIuMDcwMzkgNy45NTYwMUMwLjE5MDM4NiA5LjEzMTAxIDAuMTkwMzg3IDExLjg2OSAyLjA3MDM5IDEzLjA0NEwxNCAyMC41WiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+Cg==');
  }

  &-title {
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
  }

  &-subtitle {
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 15px;

    span + span {
      margin-left: 3px;
    }

    &:not(:first-child) {
      margin-top: 4px;
    }
  }
}

.user-popup-wrapper {
  position: relative;

  &:hover .user-popup {
    visibility: visible;
    opacity: 1;
  }
}

.settings-drop {
  min-width: 202px;
  background: #FFFFFF;
  padding: 20px;
  border-radius: 3px;
  box-shadow: -1px 0px 12px 0px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  gap: 8px;
  top: 50%;
  left: calc(100% + 27px);
  transform: translateY(-50%);

  .btn {
    height: 30px;
    width: auto;
    font-style: normal;
    font-weight: 500;
    font-size: 13px;
    line-height: 16px;
    padding: 0 24px;
    margin-left: 0!important;
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.modal-inner {
  position: relative;
}

.modal-close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  cursor: pointer;
}

.report-drop {
  padding: 4px 0;
  border-radius: 3px;
  min-width: 190px;
  left: calc(100% + 27px);
  top: 50%;
  transform: translateY(-50%);
}
</style>
